import {
  ApolloClient,
  // createHttpLink,
  InMemoryCache,
  ApolloLink,
  from,
  type Operation,
} from '@apollo/client';

import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
// eslint-disable-next-line import/extensions
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import * as Sentry from '@sentry/react';
import { notify } from 'src/utils/notifications';

import type { GraphQLFormattedError } from 'graphql';

const cache = new InMemoryCache({
  typePolicies: {
    ClientInformation: {
      keyFields: ['clientId'],
    },
    ClientWebAuthn: {
      keyFields: ['clientWebauthnId'],
    },
    Course: {
      keyFields: ['courseId'],
    },
    CourseMaterial: {
      keyFields: ['courseMaterialId'],
    },
    ClientCourseMaterialFlashcard: {
      keyFields: ['courseMaterialId', 'flashcardId'],
    },
    ClientCourseMaterialHighlight: {
      keyFields: ['courseMaterialId', 'highlightId'],
    },
    Whitelabel: {
      keyFields: ['whitelabelId'],
    },
    Request: {
      keyFields: ['requestId'],
    },
  },
});

// const httpLink = createHttpLink({
//   uri: `https://${import.meta.env.VITE_API_ORIGIN}/api`,
// });

const httpLink = createUploadLink({
  uri: `https://${import.meta.env.VITE_API_ORIGIN}/api`,
});

const stringifyExtra = (value: unknown) => {
  if (value == null) return value;
  if (['string', 'number', 'boolean'].includes(typeof value)) return value;

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const getGraphQLErrorExtras = (
  graphQLError: GraphQLFormattedError,
  operation: Operation
) => ({
  variableNames: Object.keys(operation.variables),
  graphQLErrorMessage: graphQLError.message,
  graphQLErrorPath: stringifyExtra(graphQLError.path),
  graphQLErrorLocations: stringifyExtra(graphQLError.locations),
  graphQLErrorExtensions: stringifyExtra(graphQLError.extensions),
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  const shouldNotify = operation.getContext()?.notifyOnError ?? true;
  const operationName = operation.operationName || 'unknown';

  if (graphQLErrors) {
    graphQLErrors.forEach((graphQLError) => {
      const { message, extensions } = graphQLError;
      const errors = extensions?.fields ?? {};
      const error = Object.values(errors).join(', ');
      const code = extensions?.code;

      // Auth errors — clear session and reload
      if (code === RESPONSE_CODES.FORBIDDEN) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('clientId');
        window.location.reload();
        return;
      }

      const sentryError = new Error(message);
      sentryError.name = 'GraphQLError';

      Sentry.withScope((scope) => {
        scope.setFingerprint(['graphql', operationName]);
        scope.setTags({
          type: 'graphql',
          operation: operationName,
          code: String(code ?? 'unknown'),
        });
        scope.setExtras(getGraphQLErrorExtras(graphQLError, operation));
        Sentry.captureException(sentryError);
      });

      if (!shouldNotify) return;

      notify(
        {
          title: `Something went wrong! ${!error.length ? 'Please refresh your browser.' : ''}`,
          message: error || message,
        },
        { type: 'error', autoClose: false }
      );
    });
  }

  if (networkError) {
    const signal = operation.getContext()?.fetchOptions?.signal as
      | AbortSignal
      | undefined;

    if (signal?.aborted || networkError.name === 'AbortError') {
      return;
    }

    Sentry.withScope((scope) => {
      scope.setFingerprint(['graphql-network', operationName]);
      scope.setTags({
        type: 'network',
        operation: operationName,
      });
      scope.setExtras({
        variableNames: Object.keys(operation.variables),
      });
      Sentry.captureException(networkError);
    });

    if (!shouldNotify) return;

    notify(
      {
        title: 'Network error!',
        message: networkError?.message ?? '',
      },
      { type: 'error', autoClose: false }
    );
  }
});

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

/**
 * Sentry link for automatic GraphQL performance instrumentation.
 * Tracks all queries and mutations with timing and operation details.
 */
const sentryLink = new ApolloLink((operation, forward) => {
  const definition = getMainDefinition(operation.query);
  const operationType =
    definition.kind === 'OperationDefinition'
      ? definition.operation
      : 'operation';

  return Sentry.startSpanManual(
    {
      op: `graphql.${operationType}`,
      name: operation.operationName || 'GraphQL Operation',
    },
    (span) => {
      return forward(operation).map((response) => {
        if (response.errors?.length) {
          span.setStatus({ code: 2, message: 'GraphQL Error' });
        }
        span.end();
        return response;
      });
    }
  );
});

const apolloLink = from([sentryLink, errorLink, authLink, httpLink]);

const client = new ApolloClient({
  link: apolloLink,
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
  },
});

const RESPONSE_CODES = {
  FORBIDDEN: 403,
};

export default client;
